import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Crown, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { usePlanLimits } from '../hooks/usePlanLimits';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
  currentUsage?: {
    used: number;
    limit: number;
    type: string;
  };
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ 
  isOpen, 
  onClose, 
  feature,
  currentUsage 
}) => {
  const navigate = useNavigate();
  const { subscription } = usePlanLimits();
  
  if (!isOpen) return null;

  const getPlanFeatures = (plan: string) => {
    const features: Record<string, string[]> = {
      starter: [
        '1 Buch',
        '10 QR-Codes',
        'Unbegrenzte Scans',
        'Basis-Analytics'
      ],
      growth: [
        '5 Bücher',
        '100 QR-Codes',
        'Unbegrenzte Scans',
        '3 Team-Mitglieder',
        '5 KI-Analysen/Monat',
        'Erweiterte Analytics'
      ],
      business: [
        '25 Bücher',
        '500 QR-Codes',
        'Unbegrenzte Scans',
        '10 Team-Mitglieder',
        '20 KI-Analysen/Monat',
        'White-Label Branding',
        'API-Zugang'
      ]
    };
    return features[plan] || [];
  };

  const handleUpgrade = () => {
    navigate('/qr/pricing');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          
          <div className="flex items-center">
            <Crown className="h-10 w-10 mr-3" />
            <div>
              <h2 className="text-2xl font-bold">Upgrade für mehr Features</h2>
              <p className="text-white/90 mt-1">
                {feature || 'Diese Funktion benötigt ein Upgrade'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Usage Info */}
          {currentUsage && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-orange-600 mr-2" />
                <div className="flex-1">
                  <p className="font-semibold text-orange-900">
                    Limit erreicht: {currentUsage.type}
                  </p>
                  <p className="text-sm text-orange-800 mt-1">
                    Sie nutzen bereits {currentUsage.used} von {currentUsage.limit} verfügbaren {currentUsage.type}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Plan */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Ihr aktueller Plan: {subscription?.plan === 'free' ? 'Kostenlos' : subscription?.plan || 'Kostenlos'}
            </h3>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700 mb-2">Ihre aktuellen Limits:</p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• {subscription?.limits?.books_limit || 1} {subscription?.limits?.books_limit === 1 ? 'Buch' : 'Bücher'}</li>
                <li>• {subscription?.limits?.qr_codes_limit || 2} QR-Codes</li>
                <li>• {subscription?.limits?.seats_limit || 1} {subscription?.limits?.seats_limit === 1 ? 'Benutzer' : 'Benutzer'}</li>
              </ul>
            </div>
          </div>

          {/* Upgrade Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Verfügbare Upgrades:</h3>
            
            {/* Show next plan options */}
            {(!subscription?.plan || subscription.plan === 'free') && (
              <div className="border border-blue-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-900">Starter Plan</h4>
                  <span className="text-sm font-medium text-blue-600">€7/Monat</span>
                </div>
                <ul className="space-y-1 text-sm text-gray-600">
                  {getPlanFeatures('starter').map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(!subscription?.plan || ['free', 'starter'].includes(subscription.plan)) && (
              <div className="border border-purple-200 rounded-lg p-4 hover:border-purple-300 transition-colors bg-purple-50">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-900">Growth Plan</h4>
                  <span className="text-sm font-medium text-purple-600">€23/Monat</span>
                </div>
                <ul className="space-y-1 text-sm text-gray-600">
                  {getPlanFeatures('growth').map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={handleUpgrade}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center group"
            >
              Jetzt upgraden
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Später
            </button>
          </div>

          {/* Additional Info */}
          <p className="mt-4 text-center text-sm text-gray-500">
            7 Tage kostenlos testen • Jederzeit kündbar
          </p>
        </div>
      </div>
    </div>
  );
};